import { useCallback } from "react";
import { useIntercom } from "react-use-intercom";

import {
  resetSupportChatUnreadCount,
  setSupportChatReady,
  useSupportChatState,
} from "../_libs/support-chat-store";

/**
 * Messenger attributes forced onto every boot and every update.
 *
 * `hideDefaultLauncher` turns off the vendor's own launcher, because we render
 * a draggable one instead (`SupportChatBubble`). The vendor's `boot` REPLACES
 * `window.intercomSettings` wholesale, so a single boot that omits this brings
 * the stock launcher back alongside ours — which is why there is exactly one
 * boot site in the codebase and Biome bans the vendor import everywhere else.
 */
const MESSENGER_SETTINGS = { hideDefaultLauncher: true } as const;

export type SupportChatIdentity = {
  userId: string;
  email?: string;
  name?: string;
  phone?: string;
  userHash?: string;
};

export type SupportChat = {
  /** Boot an anonymous session if none is running. Idempotent. */
  start: () => void;
  /** Boot with, or update to, an identified session. Idempotent. */
  identify: (identity: SupportChatIdentity) => void;
  /** Open the messenger, booting an anonymous session first if needed. */
  show: () => void;
  /** End the session and clear its cookies. Call on sign-out / role loss. */
  shutdown: () => void;
  /** The messenger window is open. */
  isOpen: boolean;
};

/**
 * The only door to the support-chat vendor.
 *
 * Three invariants cannot survive being re-remembered at twenty call sites:
 *
 *   - every boot must carry `MESSENGER_SETTINGS`, or the stock launcher returns;
 *   - `isReady` must mirror boot/shutdown exactly, because the bubble is gated
 *     on it and the vendor's `getVisitorId()` is not reactive;
 *   - `shutdown` must clear the unread count itself — the vendor fires no
 *     `onUnreadCountChange(0)` on shutdown, so a stale badge would survive
 *     sign-out.
 *
 * Provider-agnostic on purpose: callers say "support chat", never "Intercom".
 */
export function useSupportChat(): SupportChat {
  const {
    boot,
    update,
    show: showMessenger,
    shutdown: shutdownMessenger,
    isOpen,
  } = useIntercom();
  const isReady = useSupportChatState((state) => state.isReady);

  const start = useCallback(() => {
    if (isReady) return;
    boot({ ...MESSENGER_SETTINGS, lastRequestAt: Date.now() });
    setSupportChatReady(true);
  }, [boot, isReady]);

  const identify = useCallback(
    (identity: SupportChatIdentity) => {
      const props = {
        ...MESSENGER_SETTINGS,
        ...identity,
        lastRequestAt: Date.now(),
      };
      // `update` is a no-op before boot, and `boot` is a no-op after it — the
      // branch is load-bearing, not defensive.
      if (isReady) {
        update(props);
        return;
      }
      boot(props);
      setSupportChatReady(true);
    },
    [boot, update, isReady],
  );

  // Booting first defines "messenger not initialized" out of existence: every
  // caller can just ask for the window, wherever in the funnel they sit.
  const show = useCallback(() => {
    start();
    showMessenger();
  }, [start, showMessenger]);

  const shutdown = useCallback(() => {
    shutdownMessenger();
    setSupportChatReady(false);
    resetSupportChatUnreadCount();
  }, [shutdownMessenger]);

  return { start, identify, show, shutdown, isOpen };
}
